import * as React from "react";
import { useLocation } from "react-router";

import { PartnerNavbar } from "@/components/layout/partner-navbar";
import { PartnerSidebar } from "@/components/layout/partner-sidebar";
import { SidebarInset, SidebarProvider } from "@nt/ui/sidebar";
import { useSetupStepsLeft, type Session } from "@/lib/queries";

const SIDEBAR_STATE_COOKIE = /(?:^|;\s*)sidebar_state=(true|false)/;

/**
 * The collapse state the sidebar wrote to its `sidebar_state` cookie. Open
 * when there is no document (server render in tests) or no cookie yet.
 */
function readSidebarDefaultOpen(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(SIDEBAR_STATE_COOKIE);
  return match ? match[1] === "true" : true;
}

/**
 * The portal shell (admin `AdminShell` pattern, no command palette or chat):
 * the inset sidebar, the mobile top bar, and one content column every page
 * renders into. The provider and the inset are height-locked so only the
 * content scrolls.
 */
export function PartnerShell({
  session,
  setupStepsLeft,
  children,
}: {
  session: Session;
  /** Overrides the Settings badge; by default it follows `overview.setup`. */
  setupStepsLeft?: number;
  children: React.ReactNode;
}) {
  const [defaultOpen] = React.useState(readSidebarDefaultOpen);
  const fromOverview = useSetupStepsLeft();
  // Pages scroll in the div below, which outlives every route, so a new page
  // opens at the top by hand. A query-only change (the range picker) stays put.
  const { pathname } = useLocation();
  const scroller = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [pathname]);
  return (
    <SidebarProvider
      defaultOpen={defaultOpen}
      className="h-svh overflow-hidden"
    >
      <PartnerSidebar
        session={session}
        setupStepsLeft={setupStepsLeft ?? fromOverview}
      />
      <SidebarInset className="h-svh overflow-hidden md:peer-data-[variant=inset]:h-[calc(100svh-1rem)]">
        <PartnerNavbar session={session} />
        <div
          ref={scroller}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          <div
            data-slot="partner-content"
            className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-4 py-6 md:px-8"
          >
            {children}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
